import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Zap, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UnifiedLoginPage = () => {
  const navigate = useNavigate();
  const { coachLogin, login, platformAdminLogin } = useAuth();
  const [loading, setLoading] = useState(false);
  // Read the cached login hint synchronously at mount (avoids set-state-in-effect lint).
  const initialHint = (() => {
    try {
      const raw = localStorage.getItem('last_login_hint');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.email ? parsed : null;
    } catch (e) {
      return null;
    }
  })();
  const [loginHint, setLoginHint] = useState(initialHint);
  const [formData, setFormData] = useState({
    email: initialHint?.email || '',
    password: '',
  });

  // PWA auto-redirect: if a session token already exists in localStorage,
  // skip the login form and route directly to the user's dashboard. This is
  // critical for the home-screen install flow where the manifest's start_url
  // points here — otherwise logged-in users would see the form on every launch.
  //
  // Routing strategy:
  //  1) Specific role tokens (club_token, team_token, platform_admin_token)
  //     map cleanly to their dashboards and carry the data we need (club_info,
  //     team_teams) to construct the URL with the correct entity id.
  //  2) For plain meybra_token (coaches/parents/athletes/admins) we use the
  //     cached last_login_hint.role to route — coaches were incorrectly
  //     landing on /dashboard (parent area) because no coach_token was set.
  useEffect(() => {
    try {
      // Club admin
      if (localStorage.getItem('club_token')) {
        const info = JSON.parse(localStorage.getItem('club_info') || '{}');
        navigate(info?.id ? `/club/${info.id}` : '/unified-login', { replace: true });
        return;
      }
      // Team coach / team manager — needs a teamId from stored teams list
      if (localStorage.getItem('team_token')) {
        const teams = JSON.parse(localStorage.getItem('team_teams') || '[]');
        const firstTeamId = teams?.[0]?.team_id;
        if (firstTeamId) {
          navigate(`/team/${firstTeamId}`, { replace: true });
          return;
        }
        // No team assigned — token is stale, clear it and stay on login
        localStorage.removeItem('team_token');
        localStorage.removeItem('team_user');
        localStorage.removeItem('team_teams');
      }
      // Platform admin
      if (localStorage.getItem('platform_admin_token')) {
        navigate('/platform-admin', { replace: true });
        return;
      }
      // Fallback to meybra_token + role hint (covers coach / parent / athlete / admin)
      if (localStorage.getItem('meybra_token')) {
        const hint = JSON.parse(localStorage.getItem('last_login_hint') || '{}');
        const role = hint?.role;
        if (role === 'coach') {
          navigate('/coach', { replace: true });
        } else if (role === 'platform_admin') {
          navigate('/platform-admin', { replace: true });
        } else if (role === 'parent' || role === 'athlete') {
          navigate('/dashboard', { replace: true });
        }
        // No role hint → fall through to login form (safer than guessing)
      }
    } catch (e) {
      // graceful — fall through to login form
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);

    try {
      // Call unified login endpoint to detect role
      const res = await axios.post(`${API}/unified/login`, {
        email: formData.email.trim(),
        password: formData.password
      });

      const { token, role, redirect, user } = res.data;

      // Cache last user for "Continue as X" quick re-login (PWA-friendly)
      try {
        localStorage.setItem('last_login_hint', JSON.stringify({
          email: formData.email.trim(),
          name: user?.full_name || user?.name || '',
          role,
        }));
      } catch (storageErr) {
        /* localStorage quota / disabled — non-critical */
      }

      // Store token and set user based on detected role
      localStorage.setItem('meybra_token', token);

      if (role === 'coach') {
        // Use coachLogin to get full profile
        await coachLogin(formData.email.trim(), formData.password);
        toast.success('Welcome back!');
        navigate('/coach');
      } else if (role === 'parent' || role === 'athlete') {
        await login(formData.email.trim(), formData.password);
        toast.success('Welcome back!');
        navigate('/dashboard');
      } else if (role === 'platform_admin') {
        await platformAdminLogin(formData.email.trim(), formData.password);
        toast.success('Welcome, Admin!');
        navigate('/platform-admin');
      } else if (role === 'club_admin') {
        localStorage.setItem('meybra_token', token);
        localStorage.setItem('club_token', token);
        localStorage.setItem('club_user', JSON.stringify(user));
        localStorage.setItem('club_admin', JSON.stringify(user));
        if (user?.club_id) {
          localStorage.setItem('club_info', JSON.stringify({ id: user.club_id }));
        }
        toast.success('Welcome back!');
        navigate(redirect || '/club');
      } else if (role === 'team_coach') {
        localStorage.setItem('meybra_token', token);
        localStorage.setItem('team_token', token);
        localStorage.setItem('team_coach', JSON.stringify(user));
        localStorage.setItem('team_user', JSON.stringify(user));
        if (user?.teams) {
          localStorage.setItem('team_teams', JSON.stringify(user.teams));
        }
        toast.success('Welcome back!');
        navigate(redirect || '/team');
      } else if (role === 'team_manager') {
        localStorage.setItem('meybra_token', token);
        localStorage.setItem('team_token', token);
        localStorage.setItem('team_manager', JSON.stringify(user));
        localStorage.setItem('team_user', JSON.stringify(user));
        if (user?.teams) {
          localStorage.setItem('team_teams', JSON.stringify(user.teams));
        }
        toast.success('Welcome back!');
        navigate(redirect || '/team');
      } else {
        toast.success('Welcome back!');
        navigate(redirect || '/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div className="absolute inset-0 green-mesh opacity-20" />
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <Zap className="text-primary" size={32} />
          <span className="font-heading text-3xl">MEYBRA</span>
        </Link>
        
        <div className="bg-card border border-border/40 p-8 rounded-sm">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-6 transition-colors">
            <ArrowLeft size={20} />
            <span className="text-sm uppercase tracking-widest">Back</span>
          </Link>
          
          <h1 className="font-heading text-2xl mb-1" data-testid="unified-login-title">LOGIN</h1>
          <p className="text-muted-foreground text-sm mb-6">
            Access your account — coaches, parents, clubs, and admins
          </p>

          {/* "Continue as X" — quick re-login UI for PWA / repeat visits */}
          {loginHint && (
            <div
              className="mb-5 p-3 rounded-sm bg-primary/5 border border-primary/30 flex items-center gap-3"
              data-testid="login-hint-banner"
            >
              <div className="w-10 h-10 rounded-full bg-primary/15 text-primary flex items-center justify-center font-bold shrink-0">
                {(loginHint.name || loginHint.email || '?').charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Continue as
                </p>
                <p className="font-medium text-sm truncate">
                  {loginHint.name || loginHint.email}
                </p>
                {loginHint.name && (
                  <p className="text-[11px] text-muted-foreground truncate">{loginHint.email}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setLoginHint(null);
                  setFormData({ email: '', password: '' });
                  localStorage.removeItem('last_login_hint');
                }}
                className="text-[10px] text-muted-foreground hover:text-foreground uppercase tracking-wider shrink-0"
                data-testid="login-hint-clear"
              >
                Not you?
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                Email
              </label>
              <Input
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="bg-muted/30 border-border/40"
                data-testid="unified-login-email"
              />
            </div>
            
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                Password
              </label>
              <Input
                type="password"
                placeholder="********"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                className="bg-muted/30 border-border/40"
                data-testid="unified-login-password"
              />
            </div>
            
            <Button 
              type="submit" 
              disabled={loading}
              className="w-full bg-primary text-black hover:bg-primary/90 uppercase tracking-wider"
              data-testid="unified-login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
          
          <div className="mt-6 text-center space-y-2">
            <Link 
              to="/forgot-password" 
              className="text-sm text-muted-foreground hover:text-primary transition-colors block"
            >
              Forgot your password?
            </Link>
            <p className="text-sm text-muted-foreground">
              Don&apos;t have an account?{' '}
              <Link to="/" className="text-primary hover:underline">
                Get Started
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default UnifiedLoginPage;
